import React, { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import {
  ModernModalBackdrop,
  ModernVerticalModal,
  ModalFieldGroup,
  ModalLabel,
  ModalInput,
  ModalSelect,
  SubmitButton,
} from '../../../styles/Modal'
import { IoCloseCircleSharp, IoArrowBack } from 'react-icons/io5'
import { MdOutlineAdminPanelSettings } from 'react-icons/md'
import { FiShield, FiCheckCircle } from 'react-icons/fi'
import { carregarMarcas, MarcaItem } from '../../../services/marcas'
import { carregarDistribuidores } from '../../../services/dists'

import DistMultipleSelector from '../../Common/DistMultipleSelector'
import MarcaMultipleSelector from '../../Common/MarcaMultipleSelector'

interface PropsAdmin {
  name: string
  setName: (text: string) => void
  email: string
  setEmail: (text: string) => void
  password: string
  setPassword: (text: string) => void
  nickname: string
  setNickname: (text: string) => void
  phone: string
  setPhone: (text: string) => void
  role: string
  setRole: (text: string) => void
  loading: boolean
  handlePhoneChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  handleClick: () => void
  setCloseModal: () => void
  handleBack?: () => void
  vincMarca?: string
  setVincMarca?: (text: string) => void
  vincDist?: string | number
  setVincDist?: (distId: any) => void
}

function ModalAdmin({
  email,
  password,
  name,
  nickname,
  phone,
  loading,
  setName,
  setEmail,
  setPassword,
  setNickname,
  handlePhoneChange,
  handleClick,
  setCloseModal,
  handleBack,
  role,
  vincMarca,
  setVincMarca,
  vincDist,
  setVincDist,
}: PropsAdmin) {
  const [marcasList, setMarcasList] = useState<MarcaItem[]>([])
  const [distsList, setDistsList] = useState<any[]>([])

  useEffect(() => {
    if (role === 'conferente') {
      carregarMarcas().then((res) => setMarcasList(res))
      carregarDistribuidores().then((res) => setDistsList(res))
    }
  }, [role])

  const selectedMarcas = React.useMemo(() => {
    if (!vincMarca) return []
    return vincMarca.split(',').map((s) => s.trim()).filter(Boolean)
  }, [vincMarca])

  const handleMarcasChange = (newMarcas: string[]) => {
    if (setVincMarca) {
      setVincMarca(newMarcas.join(','))
    }
  }

  const selectedDistsConferente = React.useMemo(() => {
    if (!vincDist) return []
    return String(vincDist)
      .split(',')
      .map((s) => Number(s.trim()))
      .filter((n) => !isNaN(n) && n > 0)
  }, [vincDist])

  const handleDistsConferenteChange = (newIds: number[]) => {
    if (setVincDist) {
      setVincDist(newIds.length > 0 ? newIds.join(',') : '')
    }
  }

  const handleGeneratePassword = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%&*'
    let newPassword = ''

    for (let i = 0; i < 12; i++) {
      newPassword += chars.charAt(Math.floor(Math.random() * chars.length))
    }

    setPassword(newPassword)
    navigator.clipboard.writeText(newPassword)
    toast.success('Senha gerada e copiada para a área de transferência!')
  }

  const roleTitle = {
    conferente: 'Cadastro de Conferente',
    suporte: 'Cadastro de Suporte Operacional',
    administrador: 'Cadastro de Super Administrador',
  }[role] || `Cadastro de ${role}`

  return (
    <ModernModalBackdrop>
      <ModernVerticalModal style={{ maxWidth: '540px' }}>
        <div className="header">
          {handleBack && (
            <button className="btn-back" onClick={handleBack} type="button">
              <IoArrowBack />
            </button>
          )}
          <h3>{roleTitle}</h3>
          <button className="btn-close" onClick={setCloseModal} type="button">
            <IoCloseCircleSharp />
          </button>
        </div>

        <div className="body" style={{ maxHeight: '82vh', overflowY: 'auto' }}>
          {/* Card Didático Explicativo por Papel */}
          {role === 'conferente' && (
            <div
              style={{
                background: 'linear-gradient(135deg, rgba(236, 72, 153, 0.12), rgba(190, 24, 93, 0.12))',
                border: '1px solid rgba(244, 114, 182, 0.25)',
                borderRadius: '12px',
                padding: '14px 16px',
                display: 'flex',
                gap: '12px',
                alignItems: 'flex-start',
              }}
            >
              <MdOutlineAdminPanelSettings style={{ fontSize: '26px', color: '#f472b6', flexShrink: 0, marginTop: '2px' }} />
              <div style={{ fontSize: '12px', color: '#cbd5e1', lineHeight: '1.5' }}>
                <strong style={{ color: '#ffffff', display: 'block', marginBottom: '2px', fontSize: '13px' }}>
                  Perfil Conferente (Vínculos de Marcas & Distribuidores)
                </strong>
                O conferente pode visualizar <strong>uma ou mais marcas</strong> vinculadas e também pode ter acesso a <strong>múltiplos distribuidores</strong> específicos (ou todos, se nenhum for selecionado).
              </div>
            </div>
          )}

          {role === 'suporte' && (
            <div
              style={{
                background: 'linear-gradient(135deg, rgba(2, 132, 199, 0.12), rgba(30, 58, 138, 0.12))',
                border: '1px solid rgba(56, 189, 248, 0.25)',
                borderRadius: '12px',
                padding: '14px 16px',
                display: 'flex',
                gap: '12px',
                alignItems: 'flex-start',
              }}
            >
              <MdOutlineAdminPanelSettings style={{ fontSize: '26px', color: '#38bdf8', flexShrink: 0, marginTop: '2px' }} />
              <div style={{ fontSize: '12px', color: '#cbd5e1', lineHeight: '1.5' }}>
                <strong style={{ color: '#ffffff', display: 'block', marginBottom: '2px', fontSize: '13px' }}>
                  Perfil Suporte Operacional (Acesso Global)
                </strong>
                Acesso amplo para monitorar logs de sincronização, reprocessar arquivos com pendências e prestar suporte técnico irrestrito a todos os distribuidores.
              </div>
            </div>
          )}

          {role === 'administrador' && (
            <div
              style={{
                background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.12), rgba(180, 83, 9, 0.12))',
                border: '1px solid rgba(251, 191, 36, 0.25)',
                borderRadius: '12px',
                padding: '14px 16px',
                display: 'flex',
                gap: '12px',
                alignItems: 'flex-start',
              }}
            >
              <FiShield style={{ fontSize: '26px', color: '#f59e0b', flexShrink: 0, marginTop: '2px' }} />
              <div style={{ fontSize: '12px', color: '#cbd5e1', lineHeight: '1.5' }}>
                <strong style={{ color: '#ffffff', display: 'block', marginBottom: '2px', fontSize: '13px' }}>
                  Super Administrador (Acesso Total & Infraestrutura)
                </strong>
                Acesso irrestrito a todas as configurações de infraestrutura (SMTP/E-mail, IA), relatórios completos e autorização exclusiva para criar novos administradores.
              </div>
            </div>
          )}

          {/* Vínculo 1 do Conferente: Múltiplas Marcas (sem cod_cat) */}
          {role === 'conferente' && setVincMarca && (
            <ModalFieldGroup>
              <MarcaMultipleSelector
                marcas={marcasList}
                selectedMarcas={selectedMarcas}
                onChange={handleMarcasChange}
                label="Marcas Permitidas"
              />
              <span style={{ fontSize: '11px', color: '#94a3b8' }}>
                {selectedMarcas.length === 0
                  ? 'Nenhuma marca selecionada: o conferente terá acesso a todas as marcas.'
                  : `Filtrado estritamente por: ${selectedMarcas.join(', ')}`}
              </span>
            </ModalFieldGroup>
          )}

          {/* Vínculo 2 do Conferente: Múltiplos Distribuidores */}
          {role === 'conferente' && setVincDist && (
            <ModalFieldGroup>
              <DistMultipleSelector
                dists={distsList}
                selectedIds={selectedDistsConferente}
                onChange={handleDistsConferenteChange}
                label="Distribuidores Permitidos"
              />
              <span style={{ fontSize: '11px', color: '#94a3b8' }}>
                {selectedDistsConferente.length === 0
                  ? 'Nenhum distribuidor selecionado: o conferente poderá ver todos os distribuidores.'
                  : `${selectedDistsConferente.length} distribuidor(es) selecionado(s).`}
              </span>
            </ModalFieldGroup>
          )}

          <ModalFieldGroup>
            <ModalLabel>Usuário (Login) *</ModalLabel>
            <ModalInput
              type="text"
              value={nickname}
              onChange={(e) => {
                const value = e.target.value.replace(/\s/g, '')
                setNickname(value)
              }}
              placeholder="Digite o login de acesso"
            />
          </ModalFieldGroup>

          <ModalFieldGroup>
            <ModalLabel>Nome Completo *</ModalLabel>
            <ModalInput
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Digite o nome completo"
              style={{ textTransform: 'capitalize' }}
            />
          </ModalFieldGroup>

          <ModalFieldGroup>
            <ModalLabel>Senha *</ModalLabel>
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
              <ModalInput
                type="text"
                value={password}
                placeholder="Gerar senha ou digitar"
                onChange={(e) => setPassword(e.target.value)}
                style={{ width: '100%', paddingRight: '120px' }}
              />
              <button
                type="button"
                onClick={handleGeneratePassword}
                style={{
                  position: 'absolute',
                  right: '8px',
                  background: role === 'administrador' ? '#d97706' : role === 'conferente' ? '#db2777' : '#0284c7',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '11px',
                  padding: '6px 12px',
                  color: '#ffffff',
                  fontWeight: 600,
                }}
              >
                Gerar & Copiar
              </button>
            </div>
          </ModalFieldGroup>

          <ModalFieldGroup>
            <ModalLabel>E-mail *</ModalLabel>
            <ModalInput
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Ex: usuario@grupogsa.com.br"
            />
          </ModalFieldGroup>

          <ModalFieldGroup>
            <ModalLabel>Telefone *</ModalLabel>
            <ModalInput
              type="text"
              value={phone}
              onChange={handlePhoneChange}
              maxLength={15}
              placeholder="(00) 00000-0000"
            />
          </ModalFieldGroup>

          <SubmitButton type="button" onClick={handleClick} disabled={loading}>
            {loading ? 'Carregando...' : `Criar ${role === 'administrador' ? 'Administrador' : role === 'conferente' ? 'Conferente' : 'Usuário'}`}
          </SubmitButton>
        </div>
      </ModernVerticalModal>
    </ModernModalBackdrop>
  )
}

export default ModalAdmin
